import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

interface Step {
  id: number;
  label: string;
  completed: boolean;
  icon?: React.ReactNode;
  gradientFrom?: string;
  gradientTo?: string;
}

interface MiniProgressIndicatorProps {
  steps: Step[];
  className?: string;
  onStepClick?: (stepId: number) => void; // Click specific step to navigate to that step
}

export const MiniProgressIndicator: React.FC<MiniProgressIndicatorProps> = ({
  steps,
  className = '',
  onStepClick
}) => {
  const completedCount = steps.filter(s => s.completed).length;
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div className={`${className}`}>
      {/* Progress Bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs text-yellow-100 mb-1">
          <span>Onboarding Progress</span>
          <span className="font-medium">{completedCount} of {steps.length}</span>
        </div>
        <div className="w-full bg-yellow-900/30 rounded-full h-1.5 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-200 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Step Circles */}
      <div className="flex items-center gap-1.5 justify-center">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <motion.div
              className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                step.completed
                  ? 'bg-green-500/30 border-2 border-green-400 shadow-lg shadow-green-500/20'
                  : `bg-gradient-to-r ${step.gradientFrom || 'from-blue-500'} ${step.gradientTo || 'to-purple-600'} border-2 border-white/30 shadow-lg`
              } ${onStepClick ? 'cursor-pointer hover:scale-110 hover:shadow-xl' : ''}`}
              whileHover={onStepClick ? { scale: 1.1 } : {}}
              whileTap={onStepClick ? { scale: 0.95 } : {}}
              onClick={() => onStepClick && onStepClick(step.id)}
              title={`${step.label}${step.completed ? ' ✓' : ''}`}
            >
              {step.completed ? (
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              ) : (
                <div className="w-4 h-4 text-white">
                  {step.icon}
                </div>
              )}
            </motion.div>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div className={`h-0.5 w-3 transition-colors duration-300 ${
                step.completed ? 'bg-green-400' : 'bg-yellow-500/30'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MiniProgressIndicator;
